import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MobileNav from '../../components/feature/MobileNav';

export default function Cart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<any[]>([]);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(cart);
  };

  const updateQuantity = (index: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    const updatedCart = [...cartItems];
    updatedCart[index].quantity = newQuantity;
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (index: number) => {
    const updatedCart = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCart);
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const calculateItemTax = (item: any) => {
    const taxRate = item.tax_rate || 0.10;
    const priceWithoutTax = item.price / (1 + taxRate);
    const taxAmount = item.price - priceWithoutTax;
    return taxAmount * item.quantity;
  };

  const calculateItemPriceWithoutTax = (item: any) => {
    const taxRate = item.tax_rate || 0.10;
    const priceWithoutTax = item.price / (1 + taxRate);
    return priceWithoutTax * item.quantity;
  };

  const subtotal = cartItems.reduce((sum, item) => sum + calculateItemPriceWithoutTax(item), 0);
  const totalTax = cartItems.reduce((sum, item) => sum + calculateItemTax(item), 0);
  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-gray-600">
            <i className="ri-arrow-left-line text-2xl"></i>
          </button>
          <h1 className="text-xl font-bold text-gray-900">{t('cart.title')}</h1>
          <div className="w-8"></div>
        </div>
      </div>

      {cartItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <i className="ri-shopping-cart-2-line text-6xl text-gray-300"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('cart.empty')}</h2>
          <p className="text-gray-500 mb-8 text-center">{t('cart.emptyDesc')}</p>
          <Link
            to="/products"
            className="bg-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            {t('cart.startShopping')}
          </Link>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="px-6 py-6">
            {cartItems.map((item, index) => {
              const taxRate = item.tax_rate || 0.10;
              const priceWithoutTax = item.price / (1 + taxRate);
              const taxAmount = item.price - priceWithoutTax;
              
              return (
                <div key={index} className="bg-white rounded-2xl p-4 mb-4 shadow-md">
                  <div className="flex gap-4">
                    <div className="relative w-24 h-24 flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-lg object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                      {item.size && (
                        <p className="text-sm text-gray-600">{t('cart.size')}: {item.size}</p>
                      )}
                      {item.flavor && (
                        <p className="text-sm text-gray-600">{t('cart.flavor')}: {item.flavor}</p>
                      )}
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Pris (exkl. moms):</span>
                          <span>{priceWithoutTax.toFixed(2)} kr</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Moms ({(taxRate * 100).toFixed(0)}%):</span>
                          <span>{taxAmount.toFixed(2)} kr</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-lg font-bold text-teal-600">{item.price} kr</span>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateQuantity(index, item.quantity - 1)}
                            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="font-semibold w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(index, item.quantity + 1)}
                            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-600 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-xl"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="px-6 py-6 bg-white mt-2">
            <h3 className="font-bold text-gray-900 mb-4">{t('cart.orderSummary')}</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-gray-700">
                <span>Delsumma (exkl. moms)</span>
                <span>{subtotal.toFixed(2)} kr</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Moms</span>
                <span>{totalTax.toFixed(2)} kr</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-bold text-gray-900">
                <span>{t('cart.total')}</span>
                <span className="text-teal-600">{total.toFixed(2)} kr</span>
              </div>
            </div>
          </div>

          {/* Checkout Button */}
          <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-40">
            <button
              onClick={() => navigate('/checkout')}
              className="w-full bg-teal-600 text-white py-4 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              {t('cart.proceedToCheckout')} ({total.toFixed(2)} kr)
            </button>
          </div>
        </>
      )}

      <MobileNav />
    </div>
  );
}